import { ReebeloStoreT } from '@lambda/reebelo';

export type CookieConsentPreferences = {
  posthog: boolean;
  segment: boolean;
  googleAnalytics: boolean;
  googleAds: boolean;
  meta: boolean;
  tiktok: boolean;
  microsoft: boolean;
};

const ALL_ACCEPTED: CookieConsentPreferences = {
  posthog: true,
  segment: true,
  googleAnalytics: true,
  googleAds: true,
  meta: true,
  tiktok: true,
  microsoft: true,
};

const ALL_REJECTED: CookieConsentPreferences = {
  posthog: false,
  segment: false,
  googleAnalytics: false,
  googleAds: false,
  meta: false,
  tiktok: false,
  microsoft: false,
};

const UK_CONSENT_KEY = 'uk_cookie_consent';
const FR_CONSENT_KEY = 'fr_cookie_consent';
const DE_CONSENT_KEY = 'de_cookie_consent';
const ES_CONSENT_KEY = 'es_cookie_consent';
const IT_CONSENT_KEY = 'it_cookie_consent';
const SE_CONSENT_KEY = 'se_cookie_consent';
const IE_CONSENT_KEY = 'ie_cookie_consent';
const NL_CONSENT_KEY = 'nl_cookie_consent';
const AT_CONSENT_KEY = 'at_cookie_consent';
const BE_CONSENT_KEY = 'be_cookie_consent';
const PT_CONSENT_KEY = 'pt_cookie_consent';
const FI_CONSENT_KEY = 'fi_cookie_consent';
const US_CONSENT_KEY = 'us_cookie_consent';
const DEV_CONSENT_KEY = 'dev_cookie_consent';
const GLOBAL_CONSENT_KEY = 'global_cookie_consent';

function getCookieConsentKey(store: ReebeloStoreT): string {
  let consentKey: string;

  switch (store) {
    case 'reebelo-uk':
      consentKey = UK_CONSENT_KEY;
      break;
    case 'reebelo-fr':
      consentKey = FR_CONSENT_KEY;
      break;
    case 'reebelo-de':
      consentKey = DE_CONSENT_KEY;
      break;
    case 'reebelo-es':
      consentKey = ES_CONSENT_KEY;
      break;
    case 'reebelo-it':
      consentKey = IT_CONSENT_KEY;
      break;
    case 'reebelo-se':
      consentKey = SE_CONSENT_KEY;
      break;
    case 'reebelo-ie':
      consentKey = IE_CONSENT_KEY;
      break;
    case 'reebelo-nl':
      consentKey = NL_CONSENT_KEY;
      break;
    case 'reebelo-at':
      consentKey = AT_CONSENT_KEY;
      break;
    case 'reebelo-be':
      consentKey = BE_CONSENT_KEY;
      break;
    case 'reebelo-pt':
      consentKey = PT_CONSENT_KEY;
      break;
    case 'reebelo-fi':
      consentKey = FI_CONSENT_KEY;
      break;
    case 'reebelo-us':
      consentKey = US_CONSENT_KEY;
      break;
    case 'reebelo-dev':
      consentKey = DEV_CONSENT_KEY;
      break;
    default:
      consentKey = GLOBAL_CONSENT_KEY;
      break;
  }

  return consentKey;
}

/**
 * US states whose visitors are shown the cookie banner (RT-17243). The US
 * stays opt-out there too: consent counts as given until they reject it.
 */
export const US_COOKIE_BANNER_STATES = ['CA', 'FL', 'PA'];

/** Whether the visitor has made a choice on this store, whatever it was. */
export function hasStoredCookieConsent(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    localStorage.getItem(
      getCookieConsentKey(process.env.NEXT_PUBLIC_STORE as ReebeloStoreT),
    ) !== null
  );
}

export function getCookieConsent(): CookieConsentPreferences | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const store = process.env.NEXT_PUBLIC_STORE as ReebeloStoreT;

  // For non-UK/FR/DE/ES/IT/SE/IE/NL/AT/US/dev stores, assume consent is given (no legal opt-in requirement)
  if (
    store !== 'reebelo-uk' &&
    store !== 'reebelo-fr' &&
    store !== 'reebelo-de' &&
    store !== 'reebelo-es' &&
    store !== 'reebelo-it' &&
    store !== 'reebelo-se' &&
    store !== 'reebelo-ie' &&
    store !== 'reebelo-nl' &&
    store !== 'reebelo-at' &&
    store !== 'reebelo-be' &&
    store !== 'reebelo-pt' &&
    store !== 'reebelo-fi' &&
    store !== 'reebelo-us' &&
    store !== 'reebelo-dev'
  ) {
    return ALL_ACCEPTED;
  }

  const raw = localStorage.getItem(getCookieConsentKey(store));

  if (!raw) {
    // CCPA (US): opt-out regime — default to accepted when no preference is stored
    if (store === 'reebelo-us') {
      return ALL_ACCEPTED;
    }

    return null;
  }

  // Migrate legacy string format: 'accepted' / 'rejected'
  if (raw === 'accepted') {
    return ALL_ACCEPTED;
  }

  if (raw === 'rejected') {
    return ALL_REJECTED;
  }

  try {
    const parsed = JSON.parse(raw);

    // Migrate legacy category-level format: { analytics: bool, advertising: bool }
    if ('analytics' in parsed && !('posthog' in parsed)) {
      return {
        posthog: parsed.analytics,
        segment: parsed.analytics,
        googleAnalytics: parsed.analytics,
        googleAds: parsed.advertising,
        meta: parsed.advertising,
        tiktok: parsed.advertising,
        microsoft: parsed.advertising,
      };
    }

    return parsed as CookieConsentPreferences;
  } catch {
    return null;
  }
}

function applyConsentSignals(prefs: CookieConsentPreferences): void {
  if (typeof window === 'undefined') {
    return;
  }
  const win = window as any;

  win.dataLayer = win.dataLayer || [];

  // Define minimal gtag if GTM hasn't loaded yet; queued updates are picked up when it does.
  win.gtag =
    win.gtag ||
    function () {
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer.push(arguments);
    };
  win.gtag('consent', 'update', {
    analytics_storage:
      prefs.posthog || prefs.segment || prefs.googleAnalytics
        ? 'granted'
        : 'denied',
    ad_storage:
      prefs.googleAds || prefs.meta || prefs.tiktok || prefs.microsoft
        ? 'granted'
        : 'denied',
    ad_user_data:
      prefs.googleAds || prefs.meta || prefs.tiktok || prefs.microsoft
        ? 'granted'
        : 'denied',
    ad_personalization:
      prefs.googleAds || prefs.meta || prefs.tiktok || prefs.microsoft
        ? 'granted'
        : 'denied',
  });
}

export function setCookieConsent(prefs: CookieConsentPreferences): void {
  localStorage.setItem(
    getCookieConsentKey(process.env.NEXT_PUBLIC_STORE as ReebeloStoreT),
    JSON.stringify(prefs),
  );
  applyConsentSignals(prefs);
}

export function hasAnalyticsConsent(): boolean {
  const prefs = getCookieConsent();

  return (
    prefs?.posthog === true ||
    prefs?.segment === true ||
    prefs?.googleAnalytics === true
  );
}

export function hasPosthogConsent(): boolean {
  return getCookieConsent()?.posthog === true;
}

export function hasSegmentConsent(): boolean {
  return getCookieConsent()?.segment === true;
}

export function hasAdvertisingConsent(): boolean {
  const prefs = getCookieConsent();

  return (
    prefs?.googleAds === true ||
    prefs?.meta === true ||
    prefs?.tiktok === true ||
    prefs?.microsoft === true
  );
}

export function initConsentSignals(): void {
  const prefs = getCookieConsent();

  if (prefs) {
    applyConsentSignals(prefs);
  }
}

export function clearPosthogCookies() {
  import('posthog-js').then(({ posthog }) => posthog.reset(true));
}

export function clearSegmentCookies() {
  document.cookie = 'ajs_anonymous_id=; Max-Age=0; path=/;';
  document.cookie = 'ajs_user_id=; Max-Age=0; path=/;';
  localStorage.removeItem('ajs_user_id');
}

export function clearGoogleAnalyticsCookies() {
  document.cookie = '_ga=; Max-Age=0; path=/;';
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();

    if (name.startsWith('_ga_')) {
      document.cookie = `${name}=; Max-Age=0; path=/;`;
    }
  });
}

export function clearGoogleAdsCookies() {
  document.cookie = '_gcl_au=; Max-Age=0; path=/;';
  document.cookie = 'test_cookie=; Max-Age=0; path=/;';
}

export function clearMetaCookies() {
  document.cookie = '_fbp=; Max-Age=0; path=/;';
}

export function clearTikTokCookies() {
  document.cookie = '_tt_enable_cookie=; Max-Age=0; path=/;';
  document.cookie = '_ttp=; Max-Age=0; path=/;';
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();

    if (name === 'ttcsid' || name.startsWith('ttcsid_')) {
      document.cookie = `${name}=; Max-Age=0; path=/;`;
    }
  });
}

export function clearMicrosoftCookies() {
  document.cookie = '_uetsid=; Max-Age=0; path=/;';
  document.cookie = '_uetvid=; Max-Age=0; path=/;';
  document.cookie = 'MR=; Max-Age=0; path=/;';
  document.cookie = 'MUID=; Max-Age=0; path=/;';
}

export function clearPersistentCookies() {
  clearPosthogCookies();
  clearSegmentCookies();
  clearGoogleAnalyticsCookies();
  clearGoogleAdsCookies();
  clearMetaCookies();
  clearTikTokCookies();
  clearMicrosoftCookies();
}
